'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { gsap } from '@/lib/gsap';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { supportCopy } from '@/lib/i18n/pages/support';
import { SupportForm } from './SupportForm';

// Split a display title into animated pieces. Arabic script must never be
// split per letter (it breaks joining), so RTL renders the whole string.
function pieces(title: string, isRtl: boolean): string[] {
  return isRtl ? [title] : title.split('');
}

export function SupportPageClient() {
  const { locale, dir, href } = useLocale();
  const isRtl = dir === 'rtl';
  const t = pageCopy(supportCopy, locale);
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const helpTitleRef = useRef<HTMLHeadingElement>(null);
  const faqTitleRef = useRef<HTMLHeadingElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!heroRef.current) return;

    const ctx = gsap.context(() => {
      // Animate hero title letters
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);

        shuffledLetters.forEach((letter, index) => {
          gsap.to(letter, {
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
            delay: 0.3 + index * 0.03,
          });
        });

        if (!isRtl) {
          setTimeout(() => {
            applyHoverEffect(letters);
          }, 1500);
        }
      }

      // Animate subtitle
      if (subtitleRef.current) {
        gsap.set(subtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(subtitleRef.current, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          delay: 1.2,
          ease: 'power3.out',
        });
      }

      // Animate help section title on scroll
      const helpLetters = helpTitleRef.current?.querySelectorAll('.help-letter');
      if (helpLetters) {
        gsap.set(helpLetters, { scaleY: 0.3, opacity: 0 });

        gsap.to(helpLetters, {
          scrollTrigger: {
            trigger: helpTitleRef.current,
            start: 'top 80%',
          },
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: {
            each: 0.03,
            from: 'random',
          },
          ease: 'power3.out',
          onComplete: () => {
            if (!isRtl) applyHoverEffect(helpLetters);
          },
        });
      }

      // Animate FAQ section title on scroll
      const faqLetters = faqTitleRef.current?.querySelectorAll('.faq-letter');
      if (faqLetters) {
        gsap.set(faqLetters, { scaleY: 0.3, opacity: 0 });

        gsap.to(faqLetters, {
          scrollTrigger: {
            trigger: faqTitleRef.current,
            start: 'top 80%',
          },
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: {
            each: 0.03,
            from: 'random',
          },
          ease: 'power3.out',
          onComplete: () => {
            if (!isRtl) applyHoverEffect(faqLetters);
          },
        });
      }
    }, heroRef);

    return () => ctx.revert();
  }, [isRtl]);

  const heroTitle = t.hero.title;

  return (
    <div ref={heroRef}>
      {/* Hero Section */}
      <section
        className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#173835' }}
      >
        {/* Small label */}
        <p className="mb-8 text-sm font-medium uppercase tracking-[0.3em] text-white/40 rtl-label">
          {t.hero.eyebrow}
        </p>

        {/* Large SUPPORT title with letter animation */}
        <h1
          ref={titleRef}
          className="rtl-display-l font-six-caps text-[calc(1rem+20vw)] leading-[0.85] text-white lowercase text-center"
        >
          {pieces(heroTitle, isRtl).map((char, i) => (
            <span
              key={i}
              className="letter inline-block"
              style={{ transformOrigin: 'center bottom' }}
            >
              {char}
            </span>
          ))}
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="max-w-3xl px-8 text-center text-lg leading-relaxed text-white/60 md:text-xl rtl-body"
          style={{ marginTop: '80px' }}
        >
          {t.hero.subtitle}
        </p>

        {/* Bottom navigation */}
        <div
          className="absolute inset-x-0"
          style={{
            bottom: 'clamp(2rem, 5vw, 3.5rem)',
            paddingInline: 'clamp(1.5rem, 8vw, 120px)',
          }}
        >
          <div className="flex items-center justify-between">
            {/* Scroll to Explore - Left */}
            <div className="flex items-center gap-3">
              <span className="text-sm tracking-wider text-white/50">{t.hero.scroll}</span>
              <svg
                className="h-4 w-4 text-white/50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {/* Get Help - Right */}
            <a
              href="#help-form"
              className="text-sm tracking-wider text-white/50 hover:text-white transition-colors"
            >
              {t.hero.getHelp}
            </a>
          </div>
        </div>
      </section>

      {/* Support Options Section */}
      <section style={{ backgroundColor: '#0c0c0c', paddingTop: '120px', paddingBottom: '120px' }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 20px' }}>
          {/* Section title */}
          <div style={{ textAlign: 'center', marginBottom: '80px', width: '100%' }}>
            <p className="text-sm font-medium uppercase tracking-[0.3em] text-white/40 mb-6 rtl-label">
              {t.quick.eyebrow}
            </p>
            <h2
              ref={faqTitleRef}
              className="rtl-display-l font-six-caps text-[calc(1rem+8vw)] leading-[0.9] text-white lowercase"
            >
              {pieces(t.quick.title, isRtl).map((char, i) => (
                <span
                  key={i}
                  className={`faq-letter inline-block ${char === ' ' ? 'mx-4' : ''}`}
                  style={{ transformOrigin: 'center bottom' }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </h2>
          </div>

          {/* Support Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full" style={{ maxWidth: '1100px' }}>
            {/* Email Support */}
            <div
              className="rounded-2xl"
              style={{
                backgroundColor: '#1a1a1a',
                padding: '48px 32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#173835',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                }}
              >
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>
                {t.quick.emailTitle}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>
                {t.quick.emailBody}
              </p>
              <a href="mailto:support@datacode.app" className="text-white hover:text-white/80 transition-colors">
                <bdi dir="ltr">support@datacode.app</bdi>
              </a>
            </div>

            {/* Documentation */}
            <div
              className="rounded-2xl"
              style={{
                backgroundColor: '#1a1a1a',
                padding: '48px 32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#173835',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                }}
              >
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>
                {t.quick.docsTitle}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>
                {t.quick.docsBody}
              </p>
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>{t.quick.docsSoon}</span>
            </div>

            {/* Phone Support */}
            <div
              className="rounded-2xl"
              style={{
                backgroundColor: '#1a1a1a',
                padding: '48px 32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#173835',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                }}
              >
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>
                {t.quick.phoneTitle}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>
                {t.quick.phoneBody}
              </p>
              <a href="tel:+9647514491008" className="text-white hover:text-white/80 transition-colors">
                <bdi dir="ltr">+964 751 449 1008</bdi>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Support Form Section */}
      <section id="help-form" style={{ backgroundColor: '#173835', paddingTop: '150px', paddingBottom: '150px' }}>
        <div className="content-padding">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-16 lg:gap-24 items-start">
            {/* Left side - Title */}
            <div className="lg:sticky lg:top-32">
              <h2
                ref={helpTitleRef}
                className="rtl-display-l font-six-caps text-[calc(1rem+10vw)] lg:text-[calc(1rem+6vw)] leading-[0.85] text-white lowercase"
              >
                {pieces(t.formSection.title, isRtl).map((char, i) => (
                  <span
                    key={i}
                    className={`help-letter inline-block ${char === ' ' ? 'mx-2' : ''}`}
                    style={{ transformOrigin: 'center bottom' }}
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                ))}
              </h2>
              <p className="text-white/60 mt-8 text-lg leading-relaxed max-w-md rtl-body">
                {t.formSection.intro}
              </p>
            </div>

            {/* Right side - Form */}
            <div>
              <SupportForm />
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section
        className="relative flex min-h-[60vh] flex-col items-center justify-center"
        style={{ backgroundColor: '#0c0c0c' }}
      >
        <p className="text-center text-sm font-medium uppercase tracking-[0.3em] text-white/50 mb-10 rtl-label">
          {t.cta.eyebrow}
        </p>
        <Link href={href('/contact')} className="group">
          <h2 className="rtl-display-l font-six-caps text-[calc(1rem+12vw)] leading-[0.85] text-white lowercase text-center transition-opacity duration-300 group-hover:opacity-80">
            {t.cta.title}
          </h2>
        </Link>
      </section>
    </div>
  );
}
